import {css, StyleSheet} from 'aphrodite';
import assert from 'assert';
import classNames from 'classnames';
import React, {useMemo} from 'react';

import {
  doBoxSchemasWithRegistryQuery,
  doContainerSchemasWithRegistryQuery,
  doLocationSchemasWithRegistryQuery,
  doPlateSchemasWithRegistryQuery,
} from 'inventory/useStorableSchemasQuery';
import {InferredLookupConfigType} from 'lookup-config/inferTypeFromLookupConfig';
import {doAllProcessTaskSchemasForSchemaConfigQuery} from 'processes/modals/utils/ProcessesAllSchemasQueryUtils';
import {doAllResultSchemasQuery} from 'results/useAllResultSchemasQuery';
import {Schema} from 'schemas/SchemaConstants';
import {doAllSchemaInterfacesQuery} from 'schemas/util/useAllSchemaInterfacesQuery';
import {doTagSchemasWithRegistryQuery} from 'schemas/util/useTagSchemasQuery';
import Chip, {ChipSize} from 'ui-library/chips/Chip';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import legacyRunModal from 'ui-library/legacyRunModal';
import {withLoadingScreen} from 'util/Promises';

import {LookupInfosDescription} from './LookupConfigDescription';
import LookupConfigModal, {BasePaneLookupObjectValidation} from './LookupConfigModal';
import {LookupConfigModalContextInfo} from './LookupConfigModalConstants';
import {
  DEFAULT_FINAL_LOOKUP_STEP_TYPES,
  DEFAULT_INTERMEDIATE_LOOKUP_STEP_TYPES,
  LookupConfigSpec,
  LookupStepInfo,
  LookupStepType,
} from './LookupConfigModalConstants';
import {LOOKUP_CHIP} from './LookupConfigStyling';
import {
  getIconForLookupStepInfo,
  getLookupInfoArrayForLookupConfig,
  getStepDisplayInfo,
  RunLookupConfigModalProps,
} from './LookupConfigUtils';
import {isLookupConfigValidForModal, lookupStepsToInfosArray} from './LookupStepInitializerUtils';

export const EDIT_ACTION_TOOLTIP_TEXT = 'Edit lookup value';

interface RunLookupConfigModalForLookupConfigSummaryChipProps {
  baseSchema?: Schema | null;
  initialLookupConfig: LookupConfigSpec | null;
  firstLookupStepTypes?: Array<LookupStepType>;
  intermediateLookupStepTypes?: Array<LookupStepType>;
  finalLookupStepTypes?: Array<LookupStepType>;
  onSubmit?: (lookupConfig: LookupConfigSpec) => void;
  getLookupConfigValidationError?: (inferredType: InferredLookupConfigType) => string | null;
  basePaneLookupObjectValidation?: BasePaneLookupObjectValidation;
  lookupConfigContext?: LookupConfigModalContextInfo;
}

export async function runLookupConfigModalForLookupConfigSummaryChip({
  baseSchema,
  initialLookupConfig,
  onSubmit,
  getLookupConfigValidationError,
  basePaneLookupObjectValidation,
  lookupConfigContext,
  firstLookupStepTypes = [LookupStepType.SCHEMA_FIELD],
  intermediateLookupStepTypes = DEFAULT_INTERMEDIATE_LOOKUP_STEP_TYPES,
  finalLookupStepTypes = DEFAULT_FINAL_LOOKUP_STEP_TYPES,
}: RunLookupConfigModalForLookupConfigSummaryChipProps): Promise<void> {
  const runLookupConfigModalParams: RunLookupConfigModalProps = {
    baseSchema,
    initialLookupConfig,
    firstLookupStepTypes,
    intermediateLookupStepTypes,
    finalLookupStepTypes,
    onRequestSubmit: ({lookupConfig}: {lookupConfig: LookupConfigSpec}) => {
      onSubmit?.(lookupConfig);
    },
    submitButtonText: 'Done',
    basePaneLookupObjectValidation,
    getLookupConfigValidationError,
    context: lookupConfigContext,
  };

  const supportingData = Promise.all([
    doAllResultSchemasQuery(),
    doBoxSchemasWithRegistryQuery(),
    doContainerSchemasWithRegistryQuery(),
    doLocationSchemasWithRegistryQuery(),
    doPlateSchemasWithRegistryQuery(),
    doTagSchemasWithRegistryQuery(),
    doAllSchemaInterfacesQuery(),
    doAllProcessTaskSchemasForSchemaConfigQuery(),
  ]);
  // Ensure all schemas are loaded
  await withLoadingScreen(supportingData);
  assert(
    isLookupConfigValidForModal(runLookupConfigModalParams),
    'Lookup config is not supported in the lookup modal'
  );
  legacyRunModal(LookupConfigModal, runLookupConfigModalParams);
}

interface BaseLookupChipProps {
  lookupStepInfos: Array<LookupStepInfo>;
  initialLookupConfig: LookupConfigSpec | null;
  baseSchema?: Schema;
  firstLookupStepTypes?: Array<LookupStepType>;
  intermediateLookupStepTypes?: Array<LookupStepType>;
  finalLookupStepTypes?: Array<LookupStepType>;
  basePaneLookupObjectValidation?: BasePaneLookupObjectValidation;
  chipSize?: ChipSize;
  customWidthPx?: number;
}

interface LookupChipPropsStatic {
  isEditable: false;
  canEditIfArchived?: never;
  getLookupConfigValidationError?: never;
  lookupConfigContext?: never;
  onLookupChanged?: never;
}

interface LookupChipPropsEditable {
  isEditable: true;
  lookupConfigContext?: LookupConfigModalContextInfo;
  getLookupConfigValidationError?: (inferredType: InferredLookupConfigType) => string | null;
  onLookupChanged?: (lookupConfig: LookupConfigSpec) => void;
  canEditIfArchived?: boolean;
}

type LookupConfigSummaryChipProps = BaseLookupChipProps & (LookupChipPropsEditable | LookupChipPropsStatic);

export default function LookupConfigSummaryChip({
  basePaneLookupObjectValidation,
  baseSchema,
  canEditIfArchived,
  chipSize = ChipSize.TABLE_CELL,
  customWidthPx,
  finalLookupStepTypes,
  firstLookupStepTypes,
  getLookupConfigValidationError,
  initialLookupConfig,
  intermediateLookupStepTypes,
  isEditable,
  lookupConfigContext,
  lookupStepInfos,
  onLookupChanged,
}: LookupConfigSummaryChipProps): JSX.Element {
  const {widthCSSClass} = useMemo(() => {
    let widthCSSClass;
    if (customWidthPx !== undefined) {
      const fixedWidthClassName = `fixedWidth--${customWidthPx}`;
      widthCSSClass = StyleSheet.create({
        [fixedWidthClassName]: {
          width: `${customWidthPx}px`,
          whiteSpace: 'nowrap',
        },
      })[fixedWidthClassName];
    }
    return {widthCSSClass};
  }, [customWidthPx]);

  const editLookupConfig = (): void => {
    if (isEditable) {
      runLookupConfigModalForLookupConfigSummaryChip({
        baseSchema,
        initialLookupConfig,
        firstLookupStepTypes,
        intermediateLookupStepTypes,
        finalLookupStepTypes,
        onSubmit: onLookupChanged,
        getLookupConfigValidationError,
        basePaneLookupObjectValidation,
        lookupConfigContext,
      });
    }
  };

  if (lookupStepInfos.length === 0 && isEditable) {
    return (
      <Chip
        icon="entity-lookup"
        text="Lookup value"
        size={chipSize}
        actionIcon="edit"
        actionTooltipText={EDIT_ACTION_TOOLTIP_TEXT}
        onAction={editLookupConfig}
        onClick={editLookupConfig}
        className={css(styles.lookupChip, widthCSSClass)}
        faded={false}
      />
    );
  }
  assert(lookupStepInfos.length > 0, 'Expected non-editable lookup config to have at least one lookup step.');
  const lastLookupStepInfo = lookupStepInfos[lookupStepInfos.length - 1];
  const icon = lastLookupStepInfo.iconToOverrideDefault ?? getIconForLookupStepInfo(lastLookupStepInfo);
  const {name, isArchived, isDeleted} = getStepDisplayInfo(lastLookupStepInfo);

  const getIsEditable = (): boolean => {
    if (!isEditable) {
      return false;
    } else if (isDeleted) {
      return false;
    } else if (isArchived) {
      return canEditIfArchived ?? false;
    } else {
      return true;
    }
  };

  const canEditLookup = getIsEditable();
  const isEditableAndArchived = isEditable && isArchived && (canEditIfArchived ?? false);

  let actionIconName: BenchlingIconName | undefined;
  let tooltipText: string | undefined;

  if (canEditLookup && !isEditableAndArchived) {
    actionIconName = 'edit';
    tooltipText = EDIT_ACTION_TOOLTIP_TEXT;
  } else if (isEditableAndArchived) {
    actionIconName = 'undo';
    tooltipText = 'Replace archived value';
  }

  return (
    <Chip
      icon={icon}
      text={name}
      size={chipSize}
      negative={isDeleted}
      actionIcon={actionIconName}
      actionTooltipText={tooltipText}
      onAction={isEditable ? editLookupConfig : undefined}
      onClick={isEditable ? editLookupConfig : undefined}
      className={classNames(css(styles.lookupChip, widthCSSClass))}
      faded={isArchived}
      popoverContent={
        <>
          <b>Lookup config</b>
          <div className={css(styles.lookupInfosDescription)}>
            <LookupInfosDescription lookupStepInfos={lookupStepInfos} />
          </div>
        </>
      }
    />
  );
}

export type StaticLookupConfigSummaryChipProps = Omit<
  BaseLookupChipProps,
  'lookupStepInfos' | 'initialLookupConfig'
> & {
  context?: LookupConfigModalContextInfo;
  lookupConfig: LookupConfigSpec;
};

export function StaticLookupConfigSummaryChip({
  basePaneLookupObjectValidation,
  baseSchema,
  chipSize = ChipSize.TABLE_CELL,
  context,
  finalLookupStepTypes,
  firstLookupStepTypes,
  intermediateLookupStepTypes,
  lookupConfig,
  customWidthPx,
}: StaticLookupConfigSummaryChipProps): JSX.Element {
  const lookupStepInfos = getLookupInfoArrayForLookupConfig(
    lookupStepsToInfosArray(lookupConfig.lookupSteps, baseSchema, context ?? {})
  );
  return (
    <LookupConfigSummaryChip
      basePaneLookupObjectValidation={basePaneLookupObjectValidation}
      baseSchema={baseSchema}
      chipSize={chipSize}
      customWidthPx={customWidthPx}
      finalLookupStepTypes={finalLookupStepTypes}
      firstLookupStepTypes={firstLookupStepTypes}
      intermediateLookupStepTypes={intermediateLookupStepTypes}
      isEditable={false}
      initialLookupConfig={lookupConfig}
      lookupStepInfos={lookupStepInfos}
    />
  );
}

const styles = StyleSheet.create({
  lookupChip: LOOKUP_CHIP,
  lookupInfosDescription: {
    marginTop: '5px',
  },
});
